import { useCreateOperator } from '../../../../../contexts/CreateOperatorContext';
import React, { ChangeEvent } from 'react';
import { SettingTag } from '../../SettingTag';
import { SettingRow } from '../../../../Manage/Operator/Settings/SettingRow';
import { Input } from '../../../../../components/Input';
import { Divider } from '../../../../../components/Divider';
import { Switch } from '../../../../../components/Switch';
import { useSort } from '../../../../../hooks/useSort';

export const AccountSettings = () => {
  const { operator, updateOperator } = useCreateOperator();
  const { sortAsc } = useSort<{ order: number }>('order');
  const classes = useStyles();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateOperator(
      'accountSettings',
      Object.assign(operator.accountSettings, {
        [event.target.name]: event.target.value,
      })
    );
  };

  const defaultInputProps: any = {
    className: classes.input,
    variant: 'contained',
    type: 'number',
    onChange: handleChange,
  };

  const tag = <SettingTag name="Accounts" color="amber" size="sm" />;

  const settings: SettingRowConfig[] = [
    {
      order: 1,
      component: (
        <Input
          name="gratisAccounts"
          min={0}
          value={operator.accountSettings?.gratisAccounts}
          {...defaultInputProps}
        />
      ),
      icon: tag,
      title: 'Gratis Accounts',
      description: 'The number of free accounts created for the operator',
      level: 'sub',
      show: true,
    },
    {
      order: 2,
      component: (
        <Input
          name="defaultCreditLimit"
          min={0}
          value={operator.accountSettings?.defaultCreditLimit}
          {...defaultInputProps}
        />
      ),
      icon: tag,
      title: 'Default Credit Limit',
      description: 'The credit limit available on all accounts',
      level: 'sub',
      show: true,
    },
    {
      order: 3,
      component: (
        <Input
          name="creditResetDay"
          min={1}
          max={31}
          value={operator.accountSettings?.creditResetDay}
          {...defaultInputProps}
        />
      ),
      icon: tag,
      title: 'Credit Reset Day',
      description:
        'The day of the month all accounts reset to the default credit limit',
      level: 'sub',
      show: true,
    },
    {
      order: 4,
      component: (
        <Switch
          onChange={(event) =>
            updateOperator(
              'accountSettings',
              Object.assign(operator.accountSettings, {
                enhancedData: event.target.checked,
              })
            )
          }
          checked={operator.accountSettings?.enhancedData}
        />
      ),
      icon: tag,
      title: 'Enhanced Data',
      description:
        'This operator has the ability to associate customer related data to accounts',
      level: 'sub',
      show: true,
    },
  ];

  const renderDivider = (index: number) => {
    if (index + 1 === settings.filter((setting) => setting.show).length) return;
    return <Divider />;
  };

  return (
    <>
      {settings
        .filter((setting) => setting.show)
        .sort(sortAsc)
        .map((setting, index) => (
          <React.Fragment key={`operator-account-setting-${index + 1}`}>
            <SettingRow
              title={setting.title}
              description={setting.description}
              icon={setting.icon}
              level={setting.level}
            >
              {setting.component}
            </SettingRow>
            {renderDivider(index)}
          </React.Fragment>
        ))}
    </>
  );
};

const useStyles = () => ({
  container: 'flex flex-col gap-2 items-center box-border w-full',
  input: 'w-[80px]',
});
